import { CART } from "./../../const/cookies.mjs";
import {
  resolveCartItemPrice,
  resolveCartOptions,
  resolveCartRouteOptions,
  resolveGiftMessage,
  resolveGiftOptionId
} from "./utils.mjs";
import { useExceptions } from "../exceptions.mjs";
import { useAuth } from "../auth.mjs";
import { useSsrCoreApp } from "../ssrControl.mjs";
import { isIdenticalAddresses } from "../addressBook.mjs";
import { sleep } from "../../utils/async.mjs";
import { extractValue } from "../../utils/collections.mjs";
import { useAlerts } from "../alerts.mjs";
import { topics, useChannels } from "../channels.mjs";
import { resolveProductPrice } from "../../utils/product.mjs";
import { toNonNullable } from "../../utils/collections.mjs";
import {
  AddConfigurableItemDocument,
  AddCustomOptionsToCartItemsDocument,
  AddItemDocument,
  AddNoteToCartDocument,
  ApplyCouponToCartDocument,
  ApplyRewardPointsToCartDocument,
  CreateCartDocument,
  CustomerCartDocument,
  GuestCartDocument,
  GuestPaymentMethodsDocument,
  MergeCartsDocument,
  PaymentMethodsDocument,
  RemoveCartItemsCustomOptionsDocument,
  RemoveCouponFromCartDocument,
  RemoveItemDocument,
  RemoveRewardPointsFromCartDocument,
  SetBillingAddressDocument,
  SetGuestEmailOnCartDocument,
  SetOrderSourceOnCartDocument,
  SetPaymentMethodOnCartDocument,
  SetShippingAddressDocument,
  SetShippingMethodDocument,
  SetWalletPaymentMethodDocument,
  UpdateCartItemsCustomOptionsDocument,
  UpdateItemDocument
} from "../../graphql/Cart.mjs";
import errorsObj from "../../exceptions.mjs";
import { SetShippingLocationOnCartDocument } from "../../graphql/StorePicker.mjs";
import { useI18n } from "vue-i18n";
import { isEmpty, isNumber, uniqBy } from "lodash-es";
import { useMutation, useQuery } from "villus";
import { computed, onMounted, ref, toRefs, watch } from "vue";
import { useCookie, useRoute, useRuntimeConfig, useState } from "#app";
export function useStoredCart() {
  const orderSource = useState("source", () => "");
  const validCart = useState("valid-cart", () => false);
  const hasInvalidItems = useState("has-valid-items", () => false);
  const isPendingCart = useState("is-pending-cart", () => false);
  const isFetchingCart = useState("is-fetching-cart", () => false);
  const paymentFetching = useState("is-fetching-payment", () => false);
  const cartState = useState("cart", () => ({
    cartId: "",
    items: [],
    total: 0,
    subtotal: 0,
    shippingFees: void 0,
    address: null,
    StoreLocation: void 0,
    hasInvalidItems: false,
    itemCount: 0,
    discounts: [],
    appliedCoupon: null,
    appliedRewardPoints: void 0,
    discount: null,
    iframeUrl: null
  }));
  function getCartItem2(sku) {
    return cartState.value.items.find((i) => sku === i.sku);
  }
  function mapCartState(cart) {
    if (!cart) {
      return cartState.value;
    }
    return {
      cartId: cart.id?.toString() || "",
      total: cart.prices?.grand_total?.value ?? cartState.value.total ?? 0,
      subtotal: cart.prices?.subtotal_including_tax?.value ?? cartState.value.subtotal ?? 0,
      // discounts: toNonNullable(cart.prices?.discounts).map(d => ({ value: d.amount.value ?? 0, label: d.label })) ?? [],
      items: (cart.items ? toNonNullable(cart.items).map(mapCartItem) : cartState.value.items) ?? [],
      address: cart.addresses?.[0] || cartState.value.address || null,
      StoreLocation: cart.store_location === void 0 ? cartState.value.StoreLocation || void 0 : cart.store_location,
      shippingFees: cart.addresses?.[0]?.selected_shipping_method?.amount.value ?? cartState.value.shippingFees,
      hasInvalidItems: cart.items?.some((item) => {
        if (!item) {
          return;
        }
        const cartItem = mapCartItem(item);
        return !cartItem.stock || cartItem.quantity > cartItem.stock;
      }),
      itemCount: cart.total_quantity ?? cartState.value.itemCount ?? 0,
      discounts: toNonNullable(cart.prices?.discounts).map((d) => ({
        value: d.amount.value ?? 0,
        label: d.label
      })) ?? [],
      /** the promocode applied to the cart */
      appliedCoupon: cart?.applied_coupons === null ? null : cart?.applied_coupons?.[0]?.code || cartState.value.appliedCoupon || void 0,
      /** the discount value after applying the promocode */
      discount: cart.prices?.discounts ? cart.prices.discounts[0]?.amount : null,
      appliedRewardPoints: cart?.applied_reward_points === null ? null : cart?.applied_reward_points ? {
        money: cart?.applied_reward_points?.money?.value || 0,
        points: cart?.applied_reward_points?.points || 0
      } : cartState.value?.appliedRewardPoints,
      iframeUrl: null
      //cart?.acceptIframeUrl ?? null,
    };
  }
  function patchCartState(newState) {
    Object.keys(newState).forEach((key) => {
      cartState.value[key] = newState[key];
    });
  }
  return {
    cartState,
    orderSource,
    validCart,
    hasInvalidItems,
    isPendingCart,
    getCartItem: getCartItem2,
    patchCartState,
    mapCartState,
    paymentFetching,
    isFetchingCart
  };
}
function useCustomerCart() {
  const { execute, isFetching } = useQuery({
    query: CustomerCartDocument,
    fetchOnMount: false,
    cachePolicy: "network-only"
  });
  const route = useRoute();
  const { subscribe, unsubscribe } = useChannels();
  const { patchCartState, mapCartState, isFetchingCart } = useStoredCart();
  async function getCart(opts = {}) {
    const { data, error } = await execute({
      variables: {
        ...resolveCartOptions("customerCart"),
        ...opts
      }
    });
    if (error) {
      throw new Error(error.message);
    }
    if (data?.cart) {
      patchCartState(mapCartState(data?.cart));
    }
    const unwatch = watch(
      () => route.name,
      async (newRoute) => {
        const option = resolveCartRouteOptions("customerCart", newRoute);
        if (!isEmpty(option)) {
          await getCart(option);
          unwatch();
        }
      }
    );
    const cb = () => {
      if (process.client) {
        unwatch();
        unsubscribe(topics.auth.logout, cb);
      }
    };
    subscribe(topics.auth.logout, cb);
  }
  watch(isFetching, (value) => {
    isFetchingCart.value = value;
  });
  return {
    getCart
  };
}
function useGuestCart() {
  const { execute, isFetching } = useQuery({
    query: GuestCartDocument,
    fetchOnMount: false,
    cachePolicy: "network-only"
  });
  const route = useRoute();
  const { resetCart } = useResetCart();
  const { subscribe, unsubscribe } = useChannels();
  const { resolveException } = useExceptions("cart");
  const { cartState, patchCartState, mapCartState, isFetchingCart } = useStoredCart();
  async function getCart(opts = {}) {
    const { data, error } = await execute({
      variables: {
        cartId: cartState.value.cartId,
        ...resolveCartOptions("guestCart"),
        ...opts
      }
    });
    if (error && resolveException(error).message === errorsObj.cart.invalidCartStore.key) {
      resetCart();
    }
    if (error && resolveException(error).level === "DANGER") {
      throw new Error(error.message);
    }
    if (data?.cart) {
      patchCartState(mapCartState(data?.cart));
    }
    const unwatch = watch(
      () => route.name,
      async (newRoute) => {
        const option = resolveCartRouteOptions("guestCart", newRoute);
        if (!isEmpty(option)) {
          await getCart(option);
          unwatch();
        }
      }
    );
    const cb = () => {
      unwatch();
      unsubscribe(topics.auth.identify, cb);
    };
    subscribe(topics.auth.identify, cb);
  }
  watch(isFetching, (value) => {
    isFetchingCart.value = value;
  });
  return {
    getCart
  };
}
export function useSetupCart(cookies) {
  const { cartState } = useStoredCart();
  const { push, subscribe } = useChannels();
  const { getCart: getGuestCart } = useGuestCart();
  const { getCart: getCustomerCart } = useCustomerCart();
  const { mergeCartItems } = useMergeCartItems();
  const route = useRoute();
  if (cookies && cookies.cart && !cookies["is-secured"]) {
    cartState.value.cartId = cookies.cart;
    getGuestCart(resolveCartRouteOptions("guestCart", route.name));
  }
  subscribe(topics.auth.identify, async () => {
    if (process.client) {
      await getCustomerCart(
        resolveCartRouteOptions("customerCart", route.name)
      );
      push(topics.cart.fetchCustomerCart);
      const cartCookies = useCookie(CART);
      if (cartCookies.value) {
        await mergeCartItems(cartCookies.value);
        push(topics.cart.cartMerged);
      }
    }
  });
}
function useMergingStore() {
  const isMergingCart = useState("is-merging-cart", () => false);
  return {
    isMergingCart
  };
}
export function useMergeCartItems() {
  const { isMergingCart } = useMergingStore();
  const { execute } = useMutation(MergeCartsDocument);
  const cartCookies = useCookie(CART);
  const { cartState, patchCartState, mapCartState } = useStoredCart();
  const { success, error: alertError } = useAlerts();
  const { t } = useI18n();
  async function mergeCartItems(sourceId) {
    try {
      if (isMergingCart.value) {
        throw new Error("cartIsMerging");
      }
      isMergingCart.value = true;
      const { data, error } = await execute({
        dest: cartState.value.cartId,
        source: sourceId,
        ...resolveCartOptions("mergeCart")
      });
      if (error) {
        throw new Error(error.message);
      }
      if (data?.response) {
        cartCookies.value = null;
        patchCartState(mapCartState(data.response));
      }
      success("success", "Cart items merged successfully");
    } catch (e) {
      console.log(e);
      if (/\[GraphQL\] The cart isn't active\./.test(e.message)) {
        alertError("error", t("cartIsNotActive").toString());
      }
      if (/\[GraphQL\] Could not find a cart with ID /.test(
        e.message
      )) {
        cartCookies.value = null;
      }
    } finally {
      isMergingCart.value = false;
    }
  }
  return {
    mergeCartItems
  };
}
export function useAddCartItem() {
  const {
    execute: executeAddingSimpleProduct,
    isFetching: isFetchingSimplePrduct
  } = useMutation(AddItemDocument);
  const {
    execute: executeAddingConfigurableProduct,
    isFetching: isFetchingConfigurableProduct
  } = useMutation(AddConfigurableItemDocument);
  const { cartState, mapCartState, patchCartState } = useStoredCart();
  const { prepareCart } = usePrepareCart();
  const { resetCart } = useResetCart();
  const { cartError } = useCartErrors();
  async function addItem(item) {
    await prepareCart();
    const items = Array.isArray(item) ? item : [item];
    const result = await executeAddingSimpleProduct({
      cartId: cartState.value.cartId,
      items,
      ...resolveCartOptions("addCartItem")
    });
    if (result.error && (/\[GraphQL\] Current user does not have an active cart\./i.test(
      result.error.message
    ) || /The current user cannot perform operations on cart "(.*)"/gm.test(
      result.error.message
    ) || /\[GraphQL\] Could not find a cart with ID "(.*)"/gm.test(
      result.error.message
    ) || /\[GraphQL\] The cart isn't active\./i.test(result.error.message))) {
      resetCart();
      await addItem(item);
      return;
    }
    if (result.error) {
      throw new Error(result.error.message);
    }
    if (result.data?.response) {
      if (result.data?.response?.user_errors?.length > 0) {
        result.data?.response?.user_errors.forEach((error) => {
          cartError({ code: error?.code, message: error?.message });
        });
        throw new Error("Cart error");
      }
      patchCartState(mapCartState(result.data.response.cart));
      for (let i = 0; i < items.length; i++) {
        const cartItem = getCartItem(items[i].sku);
        if (!cartItem) {
          return;
        }
      }
    }
  }
  async function addConfigurableProductItem(item) {
    await prepareCart();
    const items = Array.isArray(item) ? item : [item];
    const result = await executeAddingConfigurableProduct({
      cartId: cartState.value.cartId,
      items,
      ...resolveCartOptions("addCartItem")
    });
    if (result.error && (/\[GraphQL\] Current user does not have an active cart\./i.test(
      result.error.message
    ) || /The current user cannot perform operations on cart "(.*)"/gm.test(
      result.error.message
    ) || /\[GraphQL\] Could not find a cart with ID "(.*)"/gm.test(
      result.error.message
    ) || /\[GraphQL\] The cart isn't active\./i.test(result.error.message))) {
      resetCart();
      await addConfigurableProductItem(item);
      return;
    }
    if (result.error) {
      throw new Error(result.error.message);
    }
    if (result.data?.response) {
      patchCartState(mapCartState(result.data.response.cart));
      for (let i = 0; i < items.length; i++) {
        const cartItem = getCartItem(items[i].data.sku);
        if (!cartItem) {
          return;
        }
      }
    }
  }
  async function add(items) {
    const input = (Array.isArray(items) ? items : [items]).filter(
      (item) => !!item.quantity
    );
    if (!input.length)
      return;
    if (input?.[0]?.parentSku) {
      await addConfigurableProductItem(
        input.map((i) => {
          return {
            data: {
              quantity: i.quantity,
              sku: i.sku,
              parent_sku: i.parentSku
            },
            parent_sku: i.parentSku,
            customizable_options: i.customizable_options
          };
        })
      );
      return;
    }
    await addItem(
      input.map((i) => {
        return {
          quantity: i.quantity,
          sku: i.sku,
          entered_options: i.entered_options
        };
      })
    );
  }
  return {
    addItem,
    addConfigurableProductItem,
    add,
    isFetching: computed(
      () => isFetchingConfigurableProduct.value || isFetchingSimplePrduct.value
    )
  };
}
export function useUpdateCartItem(id) {
  const { execute, isFetching } = useMutation(UpdateItemDocument);
  const { cartState, patchCartState, mapCartState } = useStoredCart();
  const route = useRoute();
  async function updateItem(quantity) {
    const idValue = id.value ? id.value : id;
    const item = cartState.value.items.find((i) => idValue === i.id);
    if (!item) {
      if (process.env.NODE_ENV !== "production") {
        console.warn(`Could not find product with ${idValue} in cart`);
      }
      return;
    }
    const { data, error } = await execute({
      input: {
        cart_id: cartState.value.cartId,
        cart_items: [
          {
            cart_item_uid: id.value ? id.value : id,
            quantity
          }
        ]
      },
      ...resolveCartOptions("updateCartItem"),
      ...resolveCartRouteOptions("updateCartItem", route.name)
    });
    if (data?.response?.cart) {
      patchCartState(mapCartState(data?.response.cart));
    }
    if (error) {
      throw error;
    }
  }
  return {
    updateItem,
    isFetching
  };
}
export function useRemoveCartItem(uid) {
  const { execute, isFetching: isRemoving } = useMutation(RemoveItemDocument);
  const route = useRoute();
  const { cartState, patchCartState, mapCartState } = useStoredCart();
  async function removeItem() {
    const item = cartState.value.items.find((i) => String(i.id) === uid);
    if (!item) {
      if (process.env.NODE_ENV !== "production") {
        console.warn(`Could not find product with ${uid} in cart`);
      }
    }
    const result = await execute({
      input: {
        cart_id: cartState.value.cartId,
        cart_item_uid: uid
      },
      ...resolveCartOptions("removeCartItem"),
      ...resolveCartRouteOptions("removeCartItem", route.name)
    });
    if (result?.data?.response?.cart) {
      patchCartState(mapCartState(result.data.response.cart));
    }
    if (result.error) {
      throw new Error(result.error.message);
    }
  }
  return { removeItem, isRemoving };
}
export function useUpdateProductStepItem(sku, parentSku, options) {
  const { cartItemCount, itemId } = useCartItemCount(sku);
  const { updateItem } = useUpdateCartItem(itemId);
  const { add } = useAddCartItem();
  const { warn } = useAlerts();
  const { t } = useI18n();
  const isUpdatingQuantity = ref(false);
  async function updateCartQty(qty) {
    try {
      const _parentSku = extractValue(parentSku);
      if (!cartItemCount.value && qty < 0)
        throw new Error(t("qtyUnavailable").toString());
      isUpdatingQuantity.value = true;
      !cartItemCount.value ? await add({
        quantity: qty,
        sku: extractValue(sku),
        parentSku: _parentSku || "",
        entered_options: extractValue(options)?.entered_options,
        customizable_options: extractValue(options)?.customizable_options
      }) : await updateItem(qty);
    } catch (e) {
      if (e.message.includes("requested qty is not available")) {
        warn("stock", t("outOfStock").toString());
      }
      throw e;
    } finally {
      isUpdatingQuantity.value = false;
    }
  }
  return {
    updateCartQty,
    isUpdatingQuantity
  };
}
export function useCartAttributes() {
  const { cartState, isFetchingCart, paymentFetching } = useStoredCart();
  const count = computed(() => {
    return cartState.value.itemCount;
  });
  return {
    ...toRefs(cartState.value),
    paymentFetching: computed(() => paymentFetching.value),
    cartSkus: computed(() => cartState.value.items.map(({ sku }) => sku)),
    isFetchingCart,
    count,
    isStorePickup: computed(
      () => cartState.value.StoreLocation && cartState.value.address && isIdenticalAddresses(
        cartState.value.address,
        cartState.value.StoreLocation
      )
    )
  };
}
export function useCartDiscountedPrices() {
  const { cartState } = useStoredCart();
  const total = computed(() => {
    return cartState.value.subtotal - discount.value.reduce((a, b) => a + b?.value, 0);
  });
  const subtotal = computed(() => {
    return cartState.value.subtotal;
  });
  const discount = computed(() => {
    return cartState.value.discounts?.map((discount2) => ({
      label: discount2.label,
      value: discount2.value
    }));
  });
  return {
    total,
    subtotal,
    discount
  };
}
export function getCartItem(sku) {
  const { cartState } = useStoredCart();
  return cartState.value.items.find((i) => sku === i.sku);
}
export function getCartItems() {
  const { cartState } = useStoredCart();
  return cartState.value.items;
}
export function useCartItemCount(sku) {
  const { cartState } = useStoredCart();
  return {
    cartItemCount: computed(
      () => cartState.value.items.find((item) => item.sku === extractValue(sku))?.quantity
    ),
    cartItemTotalPrice: computed(
      () => cartState.value.items.find((item) => item.sku === extractValue(sku))?.totalPrice
    ),
    itemId: computed(
      () => cartState.value.items.find((item) => item.sku === extractValue(sku))?.id || ""
    ),
    item: computed(
      () => cartState.value.items.find((item) => item.sku === extractValue(sku)) || ""
    )
  };
}
function usePrepareCart() {
  const { cartState, patchCartState } = useStoredCart();
  const { execute } = useMutation(CreateCartDocument);
  const cartCookies = useCookie(CART);
  async function prepareCart() {
    if (cartState.value.cartId) {
      return;
    }
    const { data, error } = await execute();
    if (error) {
      throw new Error(error.message);
    }
    if (data?.response) {
      patchCartState({ cartId: data?.response || "" });
      cartCookies.value = data.response;
    }
  }
  return {
    prepareCart
  };
}
export function useResetCart() {
  const cartCookies = useCookie(CART);
  function resetCart() {
    clearCart();
    cartCookies.value = null;
  }
  return {
    resetCart
  };
}
export function useApplyPromocode() {
  const { execute, isFetching } = useMutation(ApplyCouponToCartDocument);
  const cartCookies = useCookie(CART);
  const { cartState, patchCartState, mapCartState } = useStoredCart();
  const { resolveException } = useExceptions("cart");
  if (cartCookies.value) {
    cartState.value.cartId = cartCookies.value;
  }
  async function applyCoupon(code) {
    const { data, error } = await execute({
      input: { cart_id: cartState.value.cartId, coupon_code: code },
      ...resolveCartOptions("applyCouponToCart")
    });
    if (error && resolveException(error).level === "DANGER") {
      throw new Error(resolveException(error).message);
    }
    patchCartState(mapCartState(data?.response?.cart));
  }
  return {
    isFetching,
    applyCoupon
  };
}
export function useRemovePromocode() {
  const { execute, isFetching } = useMutation(RemoveCouponFromCartDocument);
  const { resolveException } = useExceptions("cart");
  const { cartState, patchCartState, mapCartState } = useStoredCart();
  async function removeCode() {
    const { data, error } = await execute({
      input: { cart_id: cartState.value.cartId },
      ...resolveCartOptions("removeCouponToCart")
    });
    if (error && resolveException(error).level === "DANGER") {
      throw new Error(resolveException(error).message);
    }
    patchCartState(mapCartState(data?.response?.cart));
    return { data, error };
  }
  return {
    removeCode,
    isFetching
  };
}
export function useApplyRewardPoints() {
  const { execute, isFetching } = useMutation(ApplyRewardPointsToCartDocument);
  const { resolveException } = useExceptions("cart");
  const { cartState, patchCartState, mapCartState } = useStoredCart();
  async function applyRewardPoints() {
    const { data, error } = await execute({
      cartId: cartState.value.cartId,
      ...resolveCartOptions("applyRewardToCart")
    });
    if (error && resolveException(error).level === "DANGER") {
      throw new Error(resolveException(error).message);
    }
    patchCartState(mapCartState(data?.response?.cart));
    return {
      data,
      error
    };
  }
  return {
    isFetching,
    applyRewardPoints
  };
}
export function useRemoveRewardPoints() {
  const { execute, isFetching } = useMutation(
    RemoveRewardPointsFromCartDocument
  );
  const { resolveException } = useExceptions("cart");
  const { cartState, patchCartState, mapCartState } = useStoredCart();
  async function removeRewardPoints() {
    const { data, error } = await execute({
      cartId: cartState.value.cartId,
      ...resolveCartOptions("removeRewardFromCard")
    });
    if (error && resolveException(error).level === "DANGER") {
      throw new Error(resolveException(error).message);
    }
    patchCartState(mapCartState(data?.response?.cart));
    return {
      data,
      error
    };
  }
  return {
    isFetching,
    removeRewardPoints
  };
}
export function clearCart() {
  const { cartState } = useStoredCart();
  cartState.value.cartId = "";
  cartState.value.items = [];
  cartState.value.total = 0;
  cartState.value.subtotal = 0;
  cartState.value.address = void 0;
  cartState.value.shippingFees = void 0;
  cartState.value.itemCount = 0;
}
export function usePaymentMethods(options = computed(() => ({
  withIframeEnabled: false,
  cached: "cache-first"
}))) {
  const { isLoggedIn } = useAuth();
  const { isPageLoading } = useSsrCoreApp();
  const { cartState, patchCartState, mapCartState, paymentFetching } = useStoredCart();
  const route = useRoute();
  const {
    execute: executeGuestPaymentMethods,
    data: guestPaymentMethods,
    error: guestPaymentError,
    isFetching: isFetchingGuestPaymentMethod
  } = useQuery({
    query: GuestPaymentMethodsDocument,
    variables: {
      cartId: cartState.value.cartId
    },
    fetchOnMount: false,
    cachePolicy: options.value.cached
  });
  const {
    execute: executeCustomerPaymentMethods,
    data: customerPaymentMethods,
    error: customerPaymentError,
    isFetching: isFetchingCustomerPaymentMethods
  } = useQuery({
    query: PaymentMethodsDocument,
    fetchOnMount: false,
    cachePolicy: options.value.cached
  });
  const { execute, isFetching: isSettingPaymentMethod } = useMutation(
    SetPaymentMethodOnCartDocument
  );
  async function setPaymentMethod(code) {
    const { data } = await execute({
      input: {
        cart_id: cartState.value.cartId,
        payment_method: {
          code
        }
      },
      ...resolveCartOptions("setPaymentMethod"),
      ...resolveCartRouteOptions("setPaymentMethod", route.name),
      // TODO refactor this one.
      withExternalParameters: options.value.withIframeEnabled
    });
    patchCartState(mapCartState(data?.response?.cart));
    return data?.response;
  }
  const paymentMethods = computed(
    () => customerPaymentMethods.value?.cart?.paymentMethods || guestPaymentMethods.value?.cart?.paymentMethods || []
  );
  watch(
    isSettingPaymentMethod,
    (value) => {
      paymentFetching.value = value;
    },
    {
      immediate: true
    }
  );
  onMounted(async () => {
    while (isPageLoading.value) {
      await sleep(200);
    }
    if (isLoggedIn.value) {
      await executeCustomerPaymentMethods();
      return;
    }
    await executeGuestPaymentMethods();
  });
  const isFetchingPaymentMethod = computed(
    () => isFetchingCustomerPaymentMethods.value || isFetchingGuestPaymentMethod.value
  );
  return {
    paymentMethods,
    error: computed(
      () => customerPaymentError.value || guestPaymentError.value
    ),
    isFetching: computed(
      () => isPageLoading.value || isFetchingPaymentMethod.value || isSettingPaymentMethod.value
    ),
    setPaymentMethod
  };
}
export function useShippingMethods() {
  const { execute, isFetching } = useMutation(SetShippingMethodDocument);
  const { cartState, patchCartState, mapCartState } = useStoredCart();
  const route = useRoute();
  const shippingMethods = computed(() => {
    return uniqBy(
      toNonNullable(cartState.value.address?.available_shipping_methods).map(
        mapShippingMethod
      ) || [],
      "method_code"
    );
  });
  async function setShippingMethod(input) {
    const { data, error } = await execute({
      input: {
        cart_id: cartState.value.cartId,
        shipping_methods: [input]
      },
      ...resolveCartOptions("setShippingMethod"),
      ...resolveCartRouteOptions("setShippingMethod", route.name)
    });
    if (data?.response?.cart) {
      patchCartState(mapCartState(data?.response?.cart));
    }
    if (error) {
      throw new Error(error.message);
    }
    return {
      data,
      error
    };
  }
  return {
    setShippingMethod,
    shippingMethods,
    isFetching
  };
}
export function useSetShippingAddressOnCart() {
  const { cartState } = useStoredCart();
  const { execute, isFetching } = useMutation(SetShippingAddressDocument);
  const route = useRoute();
  async function setShippingAddressOnCart(address, storeCode) {
    const { error } = await execute({
      input: {
        cart_id: cartState.value.cartId,
        shipping_addresses: [
          isNumber(address) ? { customer_address_id: address } : { address, ...storeCode && { pickup_location_code: storeCode } }
        ]
      },
      ...resolveCartOptions("setCartAddress"),
      ...resolveCartRouteOptions("setCartAddress", route.name)
    });
    if (error) {
      throw new Error(error.message);
    }
  }
  return {
    setShippingAddressOnCart,
    isFetching
  };
}
export function useSetGuestEmailOnCart() {
  const { execute } = useMutation(SetGuestEmailOnCartDocument);
  const { cartState } = useStoredCart();
  async function setGuestEmailOnCart(email) {
    const { error, data } = await execute({
      cartId: cartState.value.cartId,
      email
    });
    if (error) {
      throw new Error(error.message);
    }
    return data;
  }
  return {
    setGuestEmailOnCart
  };
}
export function useSetBillingAddressOnCart() {
  const { execute, isFetching } = useMutation(SetBillingAddressDocument);
  const route = useRoute();
  const { cartState } = useStoredCart();
  async function setBillingAddressOnCart() {
    const { error, data } = await execute({
      input: {
        cart_id: cartState.value.cartId,
        billing_address: {
          same_as_shipping: true
        }
      },
      ...resolveCartOptions("setCartAddress"),
      ...resolveCartRouteOptions("setCartAddress", route.name)
    });
    if (error) {
      throw new Error(error.message);
    }
    return data;
  }
  return {
    setBillingAddressOnCart,
    isFetching
  };
}
export function useSetCartAddress() {
  const { setShippingAddressOnCart, isFetching: shippingAddressLoading } = useSetShippingAddressOnCart();
  const { setBillingAddressOnCart, isFetching: billingAddressLoading } = useSetBillingAddressOnCart();
  const { patchCartState, mapCartState } = useStoredCart();
  async function _setCartAddressById(addressId) {
    await setShippingAddressOnCart(addressId);
    const data = await setBillingAddressOnCart();
    if (data?.response?.cart) {
      patchCartState(mapCartState(data?.response?.cart));
    }
    return data?.response;
  }
  async function _setCartAddress(address, storeCode) {
    await setShippingAddressOnCart(address, storeCode);
    const data = await setBillingAddressOnCart();
    if (data?.response?.cart) {
      patchCartState(mapCartState(data?.response?.cart));
    }
    return data?.response;
  }
  async function setCartAddress(address, storeCode) {
    if (isNumber(address)) {
      await _setCartAddressById(address);
      return;
    }
    await _setCartAddress(address, storeCode);
  }
  return {
    setCartAddress,
    addressLoading: computed(() => shippingAddressLoading.value || billingAddressLoading.value)
  };
}
export function useSetCartStorePicker() {
  const { execute, isFetching } = useMutation(
    SetShippingLocationOnCartDocument
  );
  const { resolveException } = useExceptions("cart");
  const { cartState, patchCartState, mapCartState } = useStoredCart();
  async function setCartStorePicker(id, type) {
    const { data, error } = await execute({
      cartId: cartState.value.cartId,
      id: Number(id),
      storeId: Number(id),
      type
    });
    if (error && resolveException(error)?.level === "DANGER") {
      throw resolveException(error)?.message;
    }
    if (data?.response?.cart) {
      patchCartState(mapCartState(data?.response?.cart));
    }
  }
  return {
    setCartStorePicker,
    isFetching
  };
}
export function useEwalletPaymentMethods() {
  const { cartState, patchCartState, paymentFetching } = useStoredCart();
  const { execute, isFetching: isSettingPaymentMethod } = useMutation(
    SetWalletPaymentMethodDocument
  );
  async function setPaymentMethod(phoneNumber) {
    const { data } = await execute({
      cartId: cartState.value.cartId,
      phoneNumber
    });
    patchCartState({ iframeUrl: data?.response?.cart?.acceptIframeUrl });
    return data?.response;
  }
  watch(
    isSettingPaymentMethod,
    (value) => {
      paymentFetching.value = value;
    },
    {
      immediate: true
    }
  );
  return {
    isFetching: computed(() => isSettingPaymentMethod.value),
    setPaymentMethod
  };
}
export function useCartItemCustomizationOption(id) {
  const { cartState, patchCartState, mapCartState } = useStoredCart();
  const { execute: executeAddCustomOption } = useMutation(
    AddCustomOptionsToCartItemsDocument
  );
  const { execute: executeUpdateCustomOption } = useMutation(
    UpdateCartItemsCustomOptionsDocument
  );
  const { execute: executeRemoveCustomOption } = useMutation(
    RemoveCartItemsCustomOptionsDocument
  );
  const route = useRoute();
  async function addCustomizationOption(optionId, message) {
    const idValue = id.value ?? id;
    const result = await executeAddCustomOption({
      cartId: cartState.value.cartId,
      items: [
        {
          cart_item_uid: idValue,
          customizable_options: [
            {
              value_string: message,
              id: optionId
            }
          ]
        }
      ]
    });
    if (result.data?.response) {
      patchCartState(mapCartState(result?.data?.response.cart));
    }
  }
  async function updateCustomizationOption(optionId, message) {
    const idValue = id.value ?? id;
    const result = await executeUpdateCustomOption({
      cartId: cartState.value.cartId,
      items: [
        {
          cart_item_uid: idValue,
          customizable_options: [
            {
              value_string: message,
              id: optionId
            }
          ]
        }
      ],
      ...resolveCartOptions("UpdateCartItemsCustomOptions"),
      ...resolveCartRouteOptions("UpdateCartItemsCustomOptions", route.name)
    });
    if (result.data?.response) {
      patchCartState(mapCartState(result?.data?.response.cart));
    }
  }
  async function removeCustomizationOption(optionId) {
    const idValue = id.value ?? id;
    const result = await executeRemoveCustomOption({
      cartId: cartState.value.cartId,
      items: [
        {
          cart_item_uid: idValue,
          customizable_options: [
            {
              id: optionId,
              value_string: "N/A"
            }
          ]
        }
      ],
      ...resolveCartOptions("RemoveCartItemsCustomOptions"),
      ...resolveCartRouteOptions("RemoveCartItemsCustomOptions", route.name)
    });
    if (result.data?.response) {
      patchCartState(mapCartState(result?.data?.response.cart));
    }
  }
  return {
    updateCustomizationOption,
    addCustomizationOption,
    removeCustomizationOption
  };
}
export function useCartErrors() {
  const { error: errorAlert } = useAlerts();
  const { t } = useI18n();
  function cartError(error) {
    switch (error.code) {
      case "INSUFFICIENT_STOCK":
        errorAlert(t("stockError").toString(), error.message);
        break;
      case "UNDEFINED":
        errorAlert(t("stockError").toString(), error.message);
        break;
      default:
        errorAlert(t("stockError").toString(), error.message);
        break;
    }
  }
  return {
    cartError
  };
}
export function useIsGuestCart() {
  const { isPageLoading } = useSsrCoreApp();
  const { user } = useAuth();
  const cartCookie = useCookie(CART);
  return {
    isGuestCart: computed(
      () => !isPageLoading.value && cartCookie.value && !user.value
    )
  };
}
export function useAddNoteToCart() {
  const { cartState } = useStoredCart();
  const { execute } = useMutation(AddNoteToCartDocument);
  async function addNoteToCart(note) {
    const { data, error } = await execute({
      cartId: cartState.value.cartId,
      note
    });
    if (error) {
      throw new Error(error.message);
    }
    return {
      data,
      error
    };
  }
  return {
    addNoteToCart
  };
}
export function useSetOrderSourceOnCart() {
  const { cartState } = useStoredCart();
  const {
    public: { commerce }
  } = useRuntimeConfig();
  const { execute } = useMutation(SetOrderSourceOnCartDocument);
  async function addOrderSourceOnCart() {
    const { data, error } = await execute({
      input: {
        cart_id: cartState.value.cartId,
        order_source: commerce?.orderSource
      }
    });
    if (error) {
      throw new Error(error.message);
    }
    return {
      data,
      error
    };
  }
  return {
    addOrderSourceOnCart
  };
}
export function mapCartItem(apiItem) {
  const baseProductPrice = apiItem && apiItem?.product && apiItem?.product?.special_price && apiItem?.product?.special_to_date ? resolveProductPrice(apiItem.product) : 0;
  const unitPrice = apiItem?.prices?.price.value || baseProductPrice;
  const totalPrice = apiItem?.prices?.row_total.value || baseProductPrice * (apiItem?.quantity ?? 1);
  const totalDiscount = apiItem?.prices?.total_item_discount?.value ?? 0;
  const oldPrice = apiItem?.product?.price_range?.maximum_price?.regular_price.value ?? 0;
  const giftBoxOptionId = (apiItem && resolveGiftOptionId(apiItem))?.toString() || "";
  const giftBoxMessage = apiItem && resolveGiftMessage(apiItem) || "";
  const configurationOptions = apiItem?.__typename === "ConfigurableCartItem" ? apiItem?.configurable_options?.map((option) => {
    return { label: option?.option_label, value: option?.value_label };
  }) : [];
  return {
    type: apiItem?.product?.__typename || "",
    id: apiItem?.uid || "",
    quantity: apiItem?.quantity || 1,
    name: apiItem?.product?.name || "",
    unitPrice,
    totalPrice,
    totalDiscount,
    oldPrice,
    sku: apiItem?.product?.sku || "",
    isPendingRemoval: false,
    slug: apiItem?.product?.url_key,
    stock: apiItem?.product && resolveCartItemPrice(apiItem?.product),
    image: {
      src: apiItem?.product?.thumbnail?.url || "",
      alt: apiItem?.product?.thumbnail?.label ?? (apiItem?.product?.name || "")
    },
    cartControl: {
      max: Number(apiItem?.product?.cart_control?.max_amount) ?? Number.MAX_VALUE,
      // maximum quantity allowed in the cart
      min: Number(apiItem?.product?.cart_control?.min_amount) ?? 0,
      // minimum quantity allowed in the cart
      step: Number(apiItem?.product?.cart_control?.increment_step) ?? 1
      // quantity step
    },
    url: apiItem?.product?.url_key || "",
    brand: apiItem?.product?.brand?.name || "",
    configurationOptions,
    // Use Configurable Cart Item keys to overwrite apiItem?.product keys
    ...apiItem?.__typename === "ConfigurableCartItem" ? apiItem?.configured_variant && mapConfiguredVariant(apiItem?.configured_variant) : {},
    /**
     * Property to check if the cart item is able to be change item quantity
     */
    allowChangeQuantity: checkIfItemIsAbleToChange(apiItem),
    /**
     * Property to show that if the product is available or not
     */
    isInvalidItem: isInvalidItem(apiItem),
    giftBoxOptionId,
    giftBoxMessage,
    isGift: apiItem?.is_gift || false
  };
}
export const GLOBAL_CONFIGURATION = {
  fetchItems: true
};
function mapConfiguredVariant(product) {
  return {
    name: product.name || "",
    image: {
      src: product.thumbnail?.url || "",
      alt: product.thumbnail?.label ?? (product.name || "")
    },
    stock: product.only_x_left_in_stock || 0,
    sku: product.sku || "",
    unitPrice: product?.price_range?.maximum_price?.final_price.value ?? 0,
    oldPrice: product?.price_range?.maximum_price?.regular_price.value ?? 0
  };
}
export function mapShippingMethod(shippingMethod) {
  return {
    ...shippingMethod,
    name: shippingMethod?.method_title ?? "",
    isActive: !shippingMethod?.is_after_cutoff_time,
    minDays: shippingMethod?.min_days ?? null,
    maxDays: shippingMethod?.max_days ?? null,
    cutoffTime: shippingMethod?.cutoff_time ?? null,
    minSubTotalFreeShipping: shippingMethod?.min_subtotal_for_free_shipping ?? null
  };
}
function checkIfItemIsAbleToChange(item) {
  return item?.__typename !== "BundleCartItem";
}
function isInvalidItem(item) {
  if (item?.__typename === "BundleCartItem") {
    return false;
  }
  const stock = item?.__typename === "ConfigurableCartItem" ? item?.configured_variant?.only_x_left_in_stock || 0 : item?.product.only_x_left_in_stock || 0;
  return stock < (item?.quantity || 1);
}
